import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, X, Send, Sparkles } from "lucide-react";

const eventTypes = [
  "Nuntă",
  "Botez",
  "Cununie",
  "Aniversare",
  "Petrecere Privată",
  "Eveniment Corporate",
  "Altul"
];

export default function AvailabilityBanner() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    event_type: "Nuntă"
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        event_type: form.event_type,
        date: form.date,
        guests: 0,
        message: `Verificare disponibilitate pentru data ${form.date} (${form.event_type}).`,
        type: "vizionare"
      });
      toast.success("Cererea a fost trimisă! Verificăm disponibilitatea și te contactăm în mai puțin de 24 de ore.");
      setForm({ name: "", phone: "", email: "", date: "", event_type: "Nuntă" });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "A apărut o eroare. Te rugăm să încerci din nou.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="availability-banner"
      className="relative bg-gradient-to-r from-[#1a1608] via-[#2a2109] to-[#1a1608] border-y border-[#D4AF37]/30 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #D4AF37 0%, transparent 40%)" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 relative">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex p-3 bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30 shrink-0">
              <CalendarCheck size={26} strokeWidth={1.5} />
            </div>
            <div className="space-y-2">
              <span className="text-[10px] font-semibold tracking-[0.3em] text-[#D4AF37] uppercase flex items-center gap-2 justify-center lg:justify-start">
                <Sparkles size={11} /> Disponibilitate limitată
              </span>
              <h3 className="font-serif-lux text-2xl sm:text-3xl text-white font-medium tracking-tight">
                Data evenimentului tău este încă disponibilă?
              </h3>
              <p className="text-sm text-white/60 font-light max-w-xl">
                Verifică acum disponibilitatea și primește o ofertă personalizată în mai puțin de 24 de ore.
              </p>
            </div>
          </div>
          <button
            data-testid="check-availability-btn"
            onClick={() => setOpen(true)}
            className="shrink-0 bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase px-8 py-4 transition-all duration-300 hover:scale-105 active:scale-95 flex items-center gap-2"
          >
            <CalendarCheck size={16} />
            Verifică disponibilitatea
          </button>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div
          data-testid="availability-modal"
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !submitting && setOpen(false)}
        >
          <div
            className="bg-[#121212] border border-[#D4AF37]/30 w-full max-w-md relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 w-full h-[4px] bg-[#D4AF37]" />
            <button
              data-testid="availability-modal-close"
              onClick={() => !submitting && setOpen(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-[#D4AF37] transition-colors"
            >
              <X size={22} />
            </button>

            <div className="p-8 sm:p-10">
              <div className="mb-6 text-center">
                <span className="text-[10px] font-semibold tracking-[0.3em] text-[#D4AF37] uppercase">Verificare rapidă</span>
                <h3 className="font-serif-lux text-2xl text-white font-medium mt-2">Verifică disponibilitatea</h3>
                <div className="w-16 h-[1px] bg-[#D4AF37]/50 mx-auto mt-3" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="availability-form">
                <div className="space-y-2">
                  <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Nume Complet</label>
                  <input
                    type="text" name="name" required
                    data-testid="availability-input-name"
                    value={form.name} onChange={handleChange}
                    placeholder="Popescu Andrei"
                    className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Telefon</label>
                    <input
                      type="tel" name="phone" required
                      data-testid="availability-input-phone"
                      value={form.phone} onChange={handleChange}
                      placeholder="0720 000 000"
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Email</label>
                    <input
                      type="email" name="email" required
                      data-testid="availability-input-email"
                      value={form.email} onChange={handleChange}
                      placeholder="andrei@example.com"
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Data Eveniment</label>
                    <input
                      type="date" name="date" required
                      data-testid="availability-input-date"
                      value={form.date} onChange={handleChange}
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Tip Eveniment</label>
                    <select
                      name="event_type"
                      data-testid="availability-input-event-type"
                      value={form.event_type} onChange={handleChange}
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    >
                      {eventTypes.map((t) => (
                        <option key={t} value={t} className="bg-[#121212]">{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="submit" disabled={submitting}
                  data-testid="availability-submit-button"
                  className="w-full bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase py-4 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="animate-pulse">Se trimite...</span>
                  ) : (
                    <>
                      <Send size={14} />
                      <span>Verifică disponibilitatea</span>
                    </>
                  )}
                </button>
                <p className="text-[11px] text-white/40 text-center">Îți răspundem în mai puțin de 24 de ore. Fără obligații.</p>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
